<template>
  <main :class="mainClass">
    <div v-if="!isLoading" class="rounded-lg">
      <div class="calendaritems flex flex-col sm:flex-row items-center justify-between space-y-2 sm:space-y-0 sm:space-x-1">
        <h1 class="text-base font-semibold text-gray-900">
          <time :datetime="currentDate">{{ formattedCurrentDateDisplay }}</time>
        </h1>
        <a-badge :count="activities.length">
          <h1 class="text-base font-semibold text-gray-900">
            Actividades
          </h1>
        </a-badge>
        <a-badge :count="passengersTotal">
          <h1 class="text-base font-semibold text-gray-900">
            Pasajeros
          </h1>
        </a-badge>

        <div v-if="showShareSection" class="p-2 flex items-center gap-2">
          <div v-if="leaderIdToFilter && additionalInfo && additionalInfo?.leader?.phone">
            <a-tooltip :title="$t('Send message via WhatsApp')">
              <a-button @click="sendCSVToWhatsapp('leader')" type="primary" ghost>
                <i class="mdi mdi-whatsapp mr-1"></i> WhatsApp
              </a-button>
            </a-tooltip>
          </div>

          <div v-if="vehicleIdToFilter && additionalInfo && additionalInfo?.vehicle?.phone">
            <a-tooltip :title="$t('Send message via WhatsApp')">
            <a-button @click="sendCSVToWhatsapp('vehicle')" type="primary" ghost>
              <i class="mdi mdi-whatsapp mr-1"></i> WhatsApp
            </a-button>
            </a-tooltip>
          </div>

          <div v-if="activities.length" class="gap-1 flex">
            <a-button v-if="serviceIdToFilter && showOrderServiceButton" @click="downloadGroupedSpreadsheet" type="primary" ghost>
              <font-awesome-icon class="mr-1" icon="file-excel"></font-awesome-icon> {{$t('Service Order')}}
            </a-button>
            <a-button v-if="serviceIdToFilter && showNationalParksButton" @click="downloadGroupedSpreadsheetAllPax" type="primary" ghost>
              <font-awesome-icon class="mr-1" icon="file-excel"></font-awesome-icon> {{$t('Full Pax')}}
            </a-button>
          </div>
        </div>

        <div class="hidden md:ml-4 md:flex md:items-center">
          <button
            type="button"
            class="w-full rounded-md text-gray-900 hover:bg-gray-50 px-3 py-2 text-sm shadow focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 mr-3"
            @click="goToToday"
          >
            {{$t('today')}}
          </button>
          <div v-if="montlyMenuVisible" class="relative">
            <button
              type="button"
              class="flex items-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              id="menu-button"
              aria-expanded="false"
              aria-haspopup="true"
              @click="toggleViewMenu"
            >
              Diaria
              <svg class="-mr-1 h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path
                  fill-rule="evenodd"
                  d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                  clip-rule="evenodd"
                />
              </svg>
            </button>

            <div
              v-if="showViewMenu"
              class="absolute right-0 z-10 mt-3 w-36 origin-top-right overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
              role="menu"
              aria-orientation="vertical"
              aria-labelledby="menu-button"
              tabindex="-1"
            >
              <div class="py-1" role="none">
                <router-link
                  :to="`${calendarProgramRoute}`"
                  class="block px-4 py-2 text-sm text-gray-700"
                  role="menuitem"
                  tabindex="-1"
                >Mensual</router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="activityitems lg:grid lg:grid-cols-12 lg:gap-x-16" >
        <div class="mt-10 text-center lg:col-start-8 lg:col-end-13 lg:row-start-1 lg:mt-9 xl:col-start-10">
          <div class="flex items-center text-gray-900">
            <button
              type="button"
              class="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
              @click="prevMonth"
            >
              <span class="sr-only">Mes anterior</span>
              <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path
                  fill-rule="evenodd"
                  d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
                  clip-rule="evenodd"
                />
              </svg>
            </button>
            <div class="flex-auto text-sm font-semibold">{{ monthNames[currentMonth] }} {{ currentYear }}</div>
            <button
              type="button"
              class="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
              @click="nextMonth"
            >
              <span class="sr-only">Mes siguiente</span>
              <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path
                  fill-rule="evenodd"
                  d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                  clip-rule="evenodd"
                />
              </svg>
            </button>
          </div>
          <div class="daysofmonth mt-6 grid grid-cols-7 text-xs leading-6 text-gray-500">
            <div>L</div>
            <div>M</div>
            <div>X</div>
            <div>J</div>
            <div>V</div>
            <div>S</div>
            <div>D</div>
          </div>
          <div class="calendar-section isolate mt-2 grid grid-cols-7 gap-px rounded-lg bg-gray-200 text-sm shadow ring-1 ring-gray-200">
            <div v-for="day in calendarDays" :key="day.index" :class="dayClasses(day)">
              <button
                v-if="day.date"
                @click="selectDate(day.date)"
                class="w-full h-full"
              >
              <a-tooltip :title="countActivities(day.date)">
                <a-badge :dot="hasActivities(day.date)">
                  <time :datetime="day.date.toISOString().split('T')[0]" class="mx-auto flex h-7 w-7 items-center justify-center rounded-full" :class="{'bg-gray-900' : day.date.toDateString() === selectedDate.toDateString()}">{{ day.date.getDate() }}</time>
                </a-badge>
              </a-tooltip>
              </button>
            </div>
          </div>
          <div class="flex justify-between space-x-2 mt-4">
            <router-link :to="newProgramRoute">
              <button type="button" class="w-full rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                Nuevo programa
              </button>
            </router-link>
          </div>

        </div>
        <ol v-if="activities.length" class="mt-4 divide-y divide-gray-100 text-sm leading-6 lg:col-span-7 xl:col-span-9">
          <b-card v-for="(event, index) in activities" :key="index" class="relative space-y-6 py-1 xl:static">

            <div class="flex-auto">
              <div class="flex-auto flex items-center">
                <router-link :to="`${editProgramRoute}${event.id}`">
                  <h1 class="pt-1">{{event.activity.date}}<span v-if="event.activity.hour"> - {{event.activity.hour}}</span></h1>
                </router-link>
                <router-link :to="`${editProgramRoute}${event.id}`">
                  <h3 class="eventname pl-4 text-gray-900 xl:pr-0">{{ event.name }}</h3>
                </router-link>
                <div @click="goToEditRoute(event.activity.service.id, 'servicesEditRoute')" class="ml-2">
                  <a-tag
                    v-if="!isEmpty(event.activity.service)"
                    class="m-1 cursor-pointer"
                    color="green"
                  >
                    <a-badge>
                      <div class="mt-1"> {{event.activity.service?.name}} </div>
                    </a-badge>
                  </a-tag>
                  <span v-else>-</span>
                </div>
                <router-link :to="`${editProgramRoute}${event.id}`" class="ml-auto" role="menuitem" tabindex="-1">
                  <i class="mdi mdi-pencil"></i>
                </router-link>
              </div>

              <dl class="mt-2 flex flex-col text-gray-500 w-full md:flex-row">
                <div v-for="(item, index) in activityDetails" :key="index" :class="index > 0 ? activityClasses : 'flex flex-col space-y-1 w-full'">
                  <div class="flex sm:block sm:items-center justify-between sm:justify-center w-full">
                    <dt class="header flex items-center justify-center">
                      <span class="sm:inline hidden">{{ $t(item.label) }}</span>
                      <i :class="'mdi mdi-icon ' + item.icon + ' sm:hidden'"></i>
                    </dt>
                    <dd class="detail text-center flex-1">
                      <div v-for="(prop, index) in item.props" :key="index" class="whitespace-pre-line">
                        <div v-if="prop === 'hotel' && !isEmpty(event.activity[prop])" @click="goToEditRoute(event.activity[prop].id, 'hotelsEditRoute')" class="mt-4">
                          <a-tag
                            class="m-1 cursor-pointer"
                            color="pink"
                          >
                            <a-badge>
                              <div class="mt-1"> {{event.activity[prop]?.name}} </div>
                            </a-badge>
                          </a-tag>
                        </div>
                        <div v-else-if="prop === 'leader' && !isEmpty(event.activity[prop])" @click="goToEditRoute(event.activity[prop].id, 'leadersEditRoute')" class="mt-4">
                          <a-tag
                            class="m-1 cursor-pointer"
                            color="blue"
                          >
                            <a-badge>
                              <div class="mt-1"> {{event.activity[prop]?.name}} </div>
                            </a-badge>
                          </a-tag>
                        </div>
                        <div v-else-if="prop === 'vehicle' && !isEmpty(event.activity[prop])" @click="goToEditRoute(event.activity[prop].id, 'vehiclesEditRoute')" class="mt-4">
                          <a-tag
                            class="m-1 cursor-pointer"
                            color="orange"
                          >
                            <a-badge>
                              <div class="mt-1"> {{event.activity[prop]?.name}} </div>
                            </a-badge>
                          </a-tag>
                        </div>
                        <div v-else-if="prop === 'includes' && !isEmpty(event.activity[prop])" class="mt-4">
                          <a-tag
                            v-for="(item, index) in event.activity[prop]" :key="index"
                            class="m-1 cursor-pointer"
                            color="cyan"
                          >
                            <a-badge>
                              <div class="mt-1"> {{item.name}} </div>
                            </a-badge>
                          </a-tag>
                        </div>
                        <div v-else>
                          <span v-if="isEmpty(event.activity[prop])">
                            -
                          </span>
                          <span v-else>
                            {{ event.activity[prop] || '-' }}
                          </span>
                        </div>
                      </div>
                    </dd>
                  </div>
                </div>
              </dl>

            </div>

            <div class="passengerlist mt-4">
              <div class="font-bold">{{$t('Passengers')}}:</div>
              <div class="flex flex-wrap -space-x-1.5">
                <div v-for="(passenger, index) in event.activity.passengers" :key="index">
                  <router-link :to="`${passengerEditRoute}${passenger.id}`" class="passengertag m-1">
                    <a-tag
                      :color="passenger.primary? 'green' : 'volcano'"
                      class="cursor-pointer mt-2"
                    >
                      <a-badge>
                        <div class="mt-1">{{passenger.fullname}} {{showPlusPaxIfPassengerIsPrimary(passenger, event)}}</div>
                        <span v-if="passenger?.language?.icon" slot="count"> {{passenger?.language?.icon}} </span>
                      </a-badge>
                    </a-tag>
                  </router-link>
                </div>
              </div>

              <br>

              <a-popover :title="`${$t('Create welcome sign')}`" trigger="click">
                <template slot="content">
                  <a-button type="prymary" @click="createWelcomeSign(event.activity.passengers, 1)" class="d-block w-full ">Cartel simple</a-button>
                  <a-button type="prymary" @click="createWelcomeSign(event.activity.passengers, 2)" class="d-block w-full  mt-2">Cartel con diseño 1</a-button>
                  <a-button type="prymary" @click="createWelcomeSign(event.activity.passengers, 3)" class="d-block w-full  mt-2">Cartel con diseño 2</a-button>
                  <a-button v-for="(item, index) in backgroundSigns" :key="index" type="prymary" @click="createWelcomeSign(event.activity.passengers, 4, item)" class="d-block w-full  mt-2">
                    Cartel perzonalizado {{ index + 1 }}
                  </a-button>
                </template>
                <a-button type="primary" ghost>
                  <font-awesome-icon class="mr-1" icon="address-card"></font-awesome-icon> {{$t('Create welcome sign')}}
                </a-button>
              </a-popover>
            </div>

            <div v-if="event.activity.news" class="mt-6">
              <div class="mt-4 space-y-4">
                <div class="bg-gray-100 p-4 rounded-lg shadow">
                  <p class="text-gray-700 whitespace-pre-line">{{ event.activity.news }}</p>
                </div>
              </div>
            </div>
          </b-card>
        </ol>
        <div v-else  class="mt-4 divide-y divide-gray-100 text-sm leading-6 lg:col-span-7 xl:col-span-9">
          <button type="button" class="relative block w-full rounded-lg border-2 border-dashed border-gray-300 p-12 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
            <h1>Aún no hay actividades para este día</h1>
            <router-link :to="newProgramRoute">
              <button type="button" class="rounded-md bg-indigo-600 mt-8 px-3 py-2 text-sm font-semibold text-white shadow hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                Nuevo programa
              </button>
            </router-link>
          </button>
        </div>

      </div>
    </div>
    <b-row v-else>
      <b-col><b-skeleton type="input"></b-skeleton></b-col>
      <b-col><b-skeleton type="input"></b-skeleton></b-col>
      <b-col md="12" class="my-4">
        <div>
          <div class="d-flex flex-wrap mb-3" style="gap: 1rem">
            <span
              class="p-2 border rounded-lg d-flex align-items-center justify-content-between"
              style="gap: 1rem"
              v-for="(item, index) in 4"
              :key="index"
            >
              <b-skeleton type="avatar"></b-skeleton>
              <b-skeleton width="100px"></b-skeleton>
            </span>
          </div>
          <b-skeleton type="button" width="150px"></b-skeleton>
        </div>
      </b-col>
    </b-row>
  </main>
</template>

<script>
import moment from 'moment'
import commonsApi from '@/api/commons'

export default {
  props: {
    serviceEndpoint: {
      type: String,
      required: true
    },
    calendarProgramRoute: {
      type: String,
      required: true
    },
    newProgramRoute: {
      type: String,
      required: true
    },
    editProgramRoute: {
      type: String,
      required: true
    },
    servicesEditRoute: {
      type: String
    },
    hotelsEditRoute: {
      type: String
    },
    leadersEditRoute: {
      type: String
    },
    vehiclesEditRoute: {
      type: String
    },
    passengerEditRoute: {
      type: String,
      required: true
    },
    serviceIdToFilter: {
      type: String,
      default: null
    },
    hotelIdToFilter: {
      type: String,
      default: null
    },
    vehicleIdToFilter: {
      type: String,
      default: null
    },
    leaderIdToFilter: {
      type: String,
      default: null
    },
    showNationalParksButton: {
      type: Boolean,
      default: false
    },
    showOrderServiceButton: {
      type: Boolean,
      default: false
    },
    showWelcomeSignButton: {
      type: Boolean,
      default: false
    },
    backgroundSigns: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      activityDetails: [
        { label: 'Hotel', icon: 'mdi-map-marker', props: ['hotel'] },
        { label: 'Leader', icon: 'mdi-account-key', props: ['leader'] },
        { label: 'Code', icon: 'mdi-periscope', props: ['code'] },
        { label: 'Itinerary', icon: 'mdi-google-maps', props: ['itinerary'] },
        { label: 'Vehicle', icon: 'mdi-van-passenger', props: ['vehicle'] },
        { label: 'Includes', icon: 'mdi-ticket', props: ['includes'] }
      ],
      activityClasses: 'space-x-2 xl:ml-2.5 xl:mt-0 xl:border-l xl:border-dashed xl:border-gray-400 xl:border-opacity-50 xl:pl-2.5 w-full',
      showViewMenu: false,
      creationIsDone: false,
      currentDate: new Date(),
      selectedDate: new Date(),
      activities: [],
      monthlyTotals: [],
      additionalInfo: {},
      monthNames: [
        'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
      ],
      isLoading: true
    }
  },
  watch: {
    '$route.query': {
      handler: 'onRouteQueryChanged',
      immediate: true,
      deep: true
    }
  },
  computed: {
    urlToUploadFiles () {
      return process.env.VUE_APP_API_URL + 'upload/'
    },
    montlyMenuVisible () {
      return !(this.serviceIdToFilter || this.hotelIdToFilter || this.leaderIdToFilter || this.vehicleIdToFilter)
    },
    showShareSection () {
      // return this.activities.length > 0 || this.passengersTotal
      return this.serviceIdToFilter
    },
    mainClass () {
      return this.hotelIdToFilter || this.vehicleIdToFilter || this.leaderIdToFilter || this.serviceIdToFilter ? '' : 'wrapper-content'
    },
    passengersTotal () {
      // Contar la cantidad total de pasajeros en todas las actividades
      return this.activities.reduce((acc, activity) => {
        if (activity.activity && Array.isArray(activity.activity.passengers)) {
          return acc + activity.activity.passengers.length
        }
        return acc
      }, 0)
    },
    formattedCurrentDateDisplay () {
      // help me return the date capitalized month name in Spanish
      return this.capitalize(this.currentDate.toLocaleDateString('es-AR', { month: 'long', year: 'numeric', day: 'numeric' }))
    },
    currentMonth () {
      return this.currentDate.getMonth()
    },
    currentYear () {
      return this.currentDate.getFullYear()
    },
    calendarDays () {
      const start = new Date(this.currentYear, this.currentMonth, 1)
      const end = new Date(this.currentYear, this.currentMonth + 1, 0)
      const days = []

      // Ajuste para que la semana comience el lunes
      const startDay = start.getDay() === 0 ? 6 : start.getDay() - 1

      // Rellenar días vacíos al inicio del mes
      for (let i = 0; i < startDay; i++) {
        days.push({ date: null, index: `empty-${i}` })
      }

      // Agregar los días del mes
      for (let i = 1; i <= end.getDate(); i++) {
        days.push({ date: new Date(this.currentYear, this.currentMonth, i) })
      }

      return days
    }

  },
  methods: {
    showPlusPaxIfPassengerIsPrimary (passenger, event) {
      return passenger?.primary === true && (event.activity.passengers.length - 1 > 0) ? `+ ${event.activity.passengers.length - 1}` : ''
    },
    whatsappTextMessage (url) {
      const message = `¡Hola! Recordá que hoy tenés '${this.activities.length}' actividades y '${this.passengersTotal}' pasajeros.  Descargá el detalle ${url}.`
      console.log(message)
      return encodeURIComponent(message)
    },
    async sendCSVToWhatsapp (kind) {
      const res = await commonsApi.axiosPostWrapper(`/${this.serviceEndpoint}/generate-grouped-spreadsheet`, {
        date: this.currentDate,
        serviceIdToFilter: this.serviceIdToFilter,
        hotelIdToFilter: this.hotelIdToFilter,
        vehicleIdToFilter: this.vehicleIdToFilter,
        leaderIdToFilter: this.leaderIdToFilter
      })
      if (res.status === 200 && res.data.url) {
        const url = res.data.url
        const link = document.createElement('a')
        link.href = `https://wa.me/${this.additionalInfo[kind].phone}/?text=${this.whatsappTextMessage(url)}`
        link.target = '_blank'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    },
    async createWelcomeSign (name, option, background) {
      // Buscar los objetos primary sea true
      const primaryPassengers = name.find(passenger => passenger.primary === true)
      const namePassengers = primaryPassengers.name
      try {
        const res = await commonsApi.axiosPostWrapper(`/${this.serviceEndpoint}/create-welcome-sign`, {
          name: namePassengers,
          option: option,
          background: background
        })
        console.log('res:', res)
        if (res.status === 200 && res.data.url) {
          const url = res.data.url
          const link = document.createElement('a')
          link.href = url
          console.log('urll:', url)
          link.target = '_blank'
          link.rel = 'noopener noreferrer'
          // link.download = 'data.docx'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        }
      } catch (error) {
        console.error(error)
      }
    },
    async handleImageUpload (event, name) {
      const file = event.target.files[0]
      console.log('Selected file:', file)

      if (!file) {
        this.$message.error('No se ha seleccionado ningún archivo')
        return
      }

      const primaryPassengers = name.find(passenger => passenger.primary === true)
      console.log('Primary passenger:', primaryPassengers)

      const namePassengers = primaryPassengers ? primaryPassengers.name : ''
      console.log('Name to be used:', namePassengers)

      const formData = new FormData()
      formData.append('image', file)
      formData.append('name', namePassengers)
      formData.append('option', '4')

      console.log('FormData contents:')
      for (let [key, value] of formData.entries()) {
        console.log(key, value)
      }

      try {
        console.log('Sending request to:', `/${this.serviceEndpoint}/upload-sign`)
        const response = await commonsApi.axiosPostMultipartWrapper(`/${this.serviceEndpoint}/upload-sign`, formData)
        console.log('Response:', response)

        if (response.status === 200) {
          this.$message.success('Imagen subida exitosamente')
        } else {
          this.$message.warning(`La imagen se subió, pero hubo una respuesta inesperada del servidor: ${response.status}`)
        }
      } catch (error) {
        console.error('Error al subir la imagen:', error)

        let errorMessage = 'Error al subir la imagen'

        if (error.response) {
          console.error('Response data:', error.response.data)
          console.error('Response status:', error.response.status)
          console.error('Response headers:', error.response.headers)
          errorMessage += `: ${error.response.status}`
          if (error.response.data && error.response.data.message) {
            errorMessage += ` - ${error.response.data.message}`
          }
        } else if (error.request) {
          console.error('No response received:', error.request)
          errorMessage += ': No se recibió respuesta del servidor'
        } else {
          console.error('Error setting up request:', error.message)
          errorMessage += `: ${error.message}`
        }

        this.$message.error(errorMessage)
      }
    },
    async downloadGroupedSpreadsheet () {
      const res = await commonsApi.axiosPostWrapper(`/${this.serviceEndpoint}/generate-grouped-spreadsheet`, {
        date: this.currentDate,
        serviceIdToFilter: this.serviceIdToFilter,
        hotelIdToFilter: this.hotelIdToFilter,
        vehicleIdToFilter: this.vehicleIdToFilter,
        leaderIdToFilter: this.leaderIdToFilter
      })
      if (res.status === 200 && res.data.url) {
        const url = res.data.url
        const link = document.createElement('a')
        console.log('url:', url)
        link.href = url
        // link.download = 'data.csv'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    },
    async downloadGroupedSpreadsheetAllPax () {
      const res = await commonsApi.axiosPostWrapper(`/${this.serviceEndpoint}/generate-grouped-spreadsheet-all-pax`, {
        date: this.currentDate,
        serviceIdToFilter: this.serviceIdToFilter,
        hotelIdToFilter: this.hotelIdToFilter,
        vehicleIdToFilter: this.vehicleIdToFilter,
        leaderIdToFilter: this.leaderIdToFilter
      })
      if (res.status === 200 && res.data.url) {
        const url = res.data.url
        const link = document.createElement('a')
        link.href = url
        // link.download = 'data.csv'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    },
    goToEditRoute (id, serviceRoute) {
      this.$router.push({ path: `${this[serviceRoute]}${id}`, query: { cd: moment(this.$route.query.cd).format('YYYY-MM-DD') } }).catch(e => {})
    },
    hasActivities (day) {
      if (this.monthlyTotals.length > 0) {
        const d = moment(day).format('YYYY-MM-DD')
        const dayDetails = this.monthlyTotals.find((item) => item.date === d)
        return dayDetails?.total > 0
      }
    },
    countActivities (day) {
      if (this.monthlyTotals.length > 0) {
        const d = moment(day).format('YYYY-MM-DD')
        const dayDetails = this.monthlyTotals.find((item) => item.date === d)
        return dayDetails?.total ? dayDetails.total : null
      }
    },
    /**
     * capitalize the first letter of a string
     */
    capitalize (str) {
      return str.charAt(0).toUpperCase() + str.slice(1)
    },
    toggleViewMenu () {
      this.showViewMenu = !this.showViewMenu
    },
    isEmpty (value) {
      if (value && typeof value === 'object') {
        return Object.keys(value).length === 0
      }
      return !value || (typeof value === 'string' && value.trim().length === 0)
    },
    /**
     * This method is called when the route.query has changed, but since a change is fired each time
     * the view is started we verify that the creationIsDone flag is false before loading the table items.
     */
    onRouteQueryChanged (newQuery, oldQuery) {
      // Compare newquery with oldquery and return true if they are the same
      if (this.creationIsDone === false || JSON.stringify(newQuery) === JSON.stringify(oldQuery)) {
        return
      }
      this.initializeArgumentsFromQstring(newQuery)
      this.loadData()
    },
    prevMonth () {
      this.currentDate.setMonth(this.currentMonth - 1)
      this.currentDate = new Date(this.currentDate)
      this.selectDate(this.currentDate)
    },
    nextMonth () {
      this.currentDate.setMonth(this.currentMonth + 1)
      this.currentDate = new Date(this.currentDate)
      this.selectDate(this.currentDate)
    },
    selectDate (date) {
      this.selectedDate = date
      this.currentDate = new Date(this.selectedDate)
      this.updateQueryParams(this.currentDate)
    },
    goToToday () {
      this.selectDate(new Date())
    },
    dayClasses (day) {
      return {
        'py-1.5': true,
        'hover:bg-gray-100': day.date,
        'focus:z-10': day.date,
        'bg-white': day.date && day.date.getMonth() === this.currentMonth,
        'bg-gray-50': !day.date || day.date.getMonth() !== this.currentMonth,
        'font-semibold': day.date && day.date.toDateString() === this.selectedDate.toDateString(),
        'text-white': day.date && day.date.toDateString() === this.selectedDate.toDateString(),
        'text-indigo-600': day.date && day.date.toDateString() === new Date().toDateString() && day.date.toDateString() !== this.selectedDate.toDateString(),
        'text-gray-900': day.date && day.date.getMonth() === this.currentMonth && day.date.toDateString() !== this.selectedDate.toDateString(),
        'text-gray-400': !day.date || day.date.getMonth() !== this.currentMonth
      }
    },
    initializeArgumentsFromQstring (newQuery) {
      const cd = newQuery.cd
      if (cd) {
        try {
          this.currentDate = moment(cd).toDate()
          this.selectedDate = this.currentDate
        } catch (error) {
          console.log('Error parsing current date from query string', error)
        }
      } else {
        this.currentDate = new Date()
        this.selectedDate = this.currentDate
      }
    },
    async loadData () {
      try {
        this.isLoading = true
        const res = await commonsApi.axiosPostWrapper(`/${this.serviceEndpoint}/dailyactivities`, {
          date: this.currentDate,
          serviceIdToFilter: this.serviceIdToFilter,
          hotelIdToFilter: this.hotelIdToFilter,
          vehicleIdToFilter: this.vehicleIdToFilter,
          leaderIdToFilter: this.leaderIdToFilter
        })
        this.activities = res.data?.activities || []
        this.monthlyTotals = res.data?.monthlyTotals || []
        this.additionalInfo = res.data?.additionalInfo || {}
      } catch (error) {
        console.log('Error loading data', error)
      } finally {
        this.isLoading = false
      }
    },
    updateQueryParams (param) {
      this.$router.push({ query: { cd: moment(param).format('YYYY-MM-DD') } }).catch(e => {})
    }
  },
  created () {
    this.initializeArgumentsFromQstring(this.$route.query)
    this.loadData()
    this.creationIsDone = true
  }
}
</script>

<style scoped>
.border {
  border: 1px solid #e5e7eb;
}
.mdi {
  font-size: 1.2rem;
}
</style>
